import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, CheckCircle2, AlertCircle, Lock, Eye, EyeOff } from "lucide-react";
import Layout from "../components/Layout";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { rawUpdatePassword } from "../lib/gotrueAuth";

export default function RedefinirPassword() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [checking, setChecking] = useState(true);

  // -------------------------------------------------------------
  // Establish the recovery session from the URL (PKCE or implicit)
  // -------------------------------------------------------------
  useEffect(() => {
    if (!isSupabaseConfigured) {
      setChecking(false);
      return;
    }

    let cancelled = false;

    async function establishRecoverySession() {
      try {
        // STRICT GUARD: if the URL clearly indicates a signup/magiclink callback
        // (NOT recovery), bail out — the AuthRecoveryListener should have
        // forwarded it to /auth/callback already, but defend in depth here.
        const search0 = new URLSearchParams(window.location.search);
        const hashRaw0 = window.location.hash || "";
        const hashParams0 = new URLSearchParams(hashRaw0.startsWith("#") ? hashRaw0.slice(1) : hashRaw0);
        const type0 = search0.get("type") || hashParams0.get("type");
        if (type0 && type0 !== "recovery") {
          // Hand off to the unified callback handler.
          window.location.replace(
            "/auth/callback" + window.location.search + window.location.hash
          );
          return false;
        }

        // 1) PKCE flow — Supabase sends ?code=...
        const search = new URLSearchParams(window.location.search);
        const code = search.get("code");
        if (code) {
          const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
          // Clean ?code from URL regardless of success
          window.history.replaceState({}, document.title, "/redefinir-password");
          if (!exErr) {
            const { data } = await supabase.auth.getSession();
            if (data?.session) return true;
          }
        }

        // 2) Implicit flow — Supabase sends #access_token=...&refresh_token=...&type=recovery
        const hash = window.location.hash || "";
        if (hash) {
          const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
          const accessToken = hashParams.get("access_token");
          const refreshToken = hashParams.get("refresh_token");
          if (accessToken && refreshToken) {
            const { error: sErr } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            // Clean hash from URL
            window.history.replaceState({}, document.title, "/redefinir-password");
            if (!sErr) {
              const { data } = await supabase.auth.getSession();
              if (data?.session) return true;
            }
          }
        }

        // 3) Session might already be set (supabase-js auto-detected on init)
        const { data: cur } = await supabase.auth.getSession();
        if (cur?.session) return true;

        // 4) Last resort — wait briefly for PASSWORD_RECOVERY event from supabase-js
        return await new Promise((resolve) => {
          const timeout = setTimeout(() => {
            sub?.subscription?.unsubscribe?.();
            resolve(false);
          }, 4000);
          const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === "PASSWORD_RECOVERY" || (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION"))) {
              clearTimeout(timeout);
              sub?.subscription?.unsubscribe?.();
              resolve(true);
            }
          });
        });
      } catch (e) {
        return false;
      }
    }

    establishRecoverySession().then((ok) => {
      if (cancelled) return;
      setRecoveryReady(ok);
      setChecking(false);
    });

    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("A password deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setError("As passwords não coincidem.");
      return;
    }
    setBusy(true);
    try {
      const result = await rawUpdatePassword(password);
      if (!result.ok) {
        setError(result.error || "Não foi possível redefinir a password.");
        return;
      }
      setSuccess(true);
      setTimeout(async () => {
        try { await supabase.auth.signOut(); } catch (_) { /* noop */ }
        nav("/entrar", { replace: true });
      }, 2500);
    } catch (err) {
      setError(err?.message || "Não foi possível redefinir a password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="min-h-[70vh] flex items-center py-20">
        <div className="max-w-md mx-auto px-6 w-full">
          <div className="dossier p-8 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <KeyRound className="w-4 h-4 text-blood" />
              <p className="mono text-[10px] tracking-[0.3em] text-blood">NOVA PASSWORD</p>
            </div>
            <h1 className="headline text-3xl mb-2">Redefinir password</h1>
            <p className="text-bone-muted text-sm mb-8 leading-relaxed">
              Escolhe uma nova password segura para a tua conta.
            </p>

            {success ? (
              <div className="text-center py-4" data-testid="newpass-success">
                <CheckCircle2 className="w-14 h-14 text-blood mx-auto mb-5" />
                <p className="mono text-[11px] tracking-[0.3em] text-blood mb-3">ATUALIZADO</p>
                <h3 className="headline text-2xl mb-3">Password alterada com sucesso</h3>
                <p className="text-bone-muted text-sm leading-relaxed">A redirecionar-te para o login...</p>
              </div>
            ) : !isSupabaseConfigured ? (
              <div className="border border-blood/40 bg-blood/10 p-4 flex gap-3">
                <AlertCircle className="w-4 h-4 text-blood shrink-0 mt-0.5" />
                <p className="mono text-[11px] text-bone-dim leading-relaxed">Autenticação ainda não configurada.</p>
              </div>
            ) : checking ? (
              <div className="border border-white/10 bg-ink-200 p-5 text-center" data-testid="newpass-checking">
                <Lock className="w-8 h-8 text-bone-faded mx-auto mb-3 animate-pulse" />
                <p className="text-sm text-bone-muted leading-relaxed mono tracking-[0.15em]">A validar link de recuperação...</p>
              </div>
            ) : !recoveryReady ? (
              <div className="border border-white/10 bg-ink-200 p-5 text-center">
                <Lock className="w-8 h-8 text-bone-faded mx-auto mb-3" />
                <p className="text-sm text-bone-muted leading-relaxed mb-3">
                  Esta página tem de ser aberta a partir do link enviado por email.
                </p>
                <p className="mono text-[10px] text-bone-faded leading-relaxed mb-4 tracking-[0.15em]">
                  O link pode ter expirado (válido 1h) ou já foi usado.
                </p>
                <Link to="/recuperar-password" className="mono text-[11px] text-blood hover:underline tracking-[0.2em]" data-testid="request-new-link">
                  Pedir novo link →
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <PasswordField
                  testId="newpass-input"
                  label="Nova password"
                  value={password}
                  onChange={setPassword}
                  show={showPassword}
                  onToggle={() => setShowPassword((s) => !s)}
                  autoFocus
                />
                <PasswordField
                  testId="confirm-newpass-input"
                  label="Confirmar password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  show={showConfirm}
                  onToggle={() => setShowConfirm((s) => !s)}
                />
                {error && <p className="mono text-xs text-blood tracking-[0.15em]" data-testid="newpass-error">{String(error)}</p>}
                <ul className="mono text-[10px] text-bone-faded tracking-[0.15em] space-y-1 pt-2">
                  <li className={password.length >= 8 ? "text-bone-dim" : ""}>· mínimo 8 caracteres</li>
                  <li className={password && password === confirmPassword ? "text-bone-dim" : ""}>· passwords coincidem</li>
                </ul>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="newpass-submit"
                  className="btn-primary w-full justify-center disabled:opacity-50"
                >
                  {busy ? "A guardar..." : (<>Guardar nova password <KeyRound className="w-4 h-4" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <style>{`.cec-input{width:100%;background:#111;border:1px solid rgba(255,255,255,0.1);padding:12px 44px 12px 14px;font-family:'IBM Plex Mono',monospace;font-size:13px;color:#fff;letter-spacing:0.05em}.cec-input:focus{outline:none;border-color:#C8102E}`}</style>
    </Layout>
  );
}

function PasswordField({ testId, label, value, onChange, show, onToggle, autoFocus = false }) {
  return (
    <div>
      <label className="mono text-[10px] tracking-[0.3em] text-bone-faded block mb-2">{label.toUpperCase()}</label>
      <div className="relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          required
          minLength={8}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="cec-input"
          autoComplete="new-password"
          autoFocus={autoFocus}
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-bone-muted hover:text-blood transition-colors"
          tabIndex={-1}
          aria-label={show ? "Esconder password" : "Mostrar password"}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
